import AWS from "aws-sdk";

import env from "../constants/env";
import state from "../state/state";
import { showError2 } from "../actions/alertActions.js";
import { refreshCredentials } from "../utils/securityUtil.js";
import { getJwtToken } from "../actions/user";
import { assertAiCapabilityEnabled } from "../utils/aiCapabilityUtils";

let aiSocket;
let aiStreamCitationCarry = "";
const INLINE_SOURCE_TAG_PATTERN = /(?:\[\s*\d+\s*:\s*\d+\s*†[^\]\n]{0,200}\]|【\s*\d+\s*:\s*\d+\s*†[^】\n]{0,200}】|〖\s*\d+\s*:\s*\d+\s*†[^〗\n]{0,200}〗)/g;
const DEFAULT_IMAGE_EDIT_MODEL = "gpt-image-2.5-sunburst-2026-09-08";

function getAiAssistantFunctionName() {
	assertAiCapabilityEnabled(env.appCapabilities);
	const functionName = env.lambda && env.lambda.aiAssistant;
	if (typeof functionName !== "string" || functionName.trim() === "") {
		throw new Error("AI is enabled, but its backend function is not configured for this environment.");
	}
	return functionName;
}

function resetAiStreamSanitizer() {
	aiStreamCitationCarry = "";
}

// Logout ends the assistant conversation, so the next session (another user, or the same user in another
// org) starts empty. The socket was opened with the previous user's identityId, so it is closed rather than
// reused, with its handlers detached first so a late chunk cannot land in the next session.
export function resetAiAssistantSession(tree = state) {
	if (aiSocket) {
		const socket = aiSocket;
		aiSocket = null;
		socket.onmessage = null;
		socket.onopen = null;
		socket.onclose = null;
		try {
			socket.close();
		} catch (err) {
			console.warn("Unable to close the AI assistant socket", err);
		}
	}
	resetAiStreamSanitizer();
	tree.set(["appState", "aiAssistantResponse"], "");
	tree.set(["appState", "aiAssistantResponseLast"], "");
	tree.set(["appState", "aiAssistantLaunchRequest"], null);
	tree.set(["appState", "aiAssistantFocusedTarget"], null);
}

function sanitizeCitationArtifacts(text = "") {
	let cleanedText = typeof text === "string" ? text : `${text || ""}`;
	if (cleanedText === "") {
		return "";
	}

	// Strip OpenAI file-search inline citation markers such as:
	// [4:0†source], 【4:0†source】, 〖4:0†source〗
	cleanedText = cleanedText.replace(INLINE_SOURCE_TAG_PATTERN, "");

	// Normalize whitespace left behind after removing citation markers.
	cleanedText = cleanedText.replace(/\s+([,.;!?])/g, "$1");
	cleanedText = cleanedText.replace(/[ \t]{2,}/g, " ");
	cleanedText = cleanedText.replace(/\n{3,}/g, "\n\n");

	return cleanedText;
}

function sanitizeAssistantStreamChunk(chunk = "") {
	const mergedChunk = `${aiStreamCitationCarry}${typeof chunk === "string" ? chunk : `${chunk || ""}`}`;
	aiStreamCitationCarry = "";

	if (mergedChunk === "") {
		return "";
	}

	let processChunk = mergedChunk;

	// Hold trailing partial citation marker until the next chunk arrives.
	const lastOpenIndex = Math.max(
		processChunk.lastIndexOf("["),
		processChunk.lastIndexOf("【"),
		processChunk.lastIndexOf("〖")
	);

	if (lastOpenIndex > -1) {
		const trailingText = processChunk.slice(lastOpenIndex);
		const hasClosingBracket =
			trailingText.indexOf("]") > -1 ||
			trailingText.indexOf("】") > -1 ||
			trailingText.indexOf("〗") > -1;
		const looksLikeCitationStart = /^(?:\[\s*\d{0,4}\s*:\s*\d{0,4}(?:\s*†.*)?|【\s*\d{0,4}\s*:\s*\d{0,4}(?:\s*†.*)?|〖\s*\d{0,4}\s*:\s*\d{0,4}(?:\s*†.*)?)$/.test(trailingText.trim());

		if (!hasClosingBracket && looksLikeCitationStart) {
			aiStreamCitationCarry = trailingText;
			processChunk = processChunk.slice(0, lastOpenIndex);
		}
	}

	return sanitizeCitationArtifacts(processChunk);
}

export function assistantsCreate() {
	assertAiCapabilityEnabled(env.appCapabilities);
	refreshCredentials().then(() => {
		// Let's append the checklist id to the name
		let arrPath = state.select("selectedNodePath").get();
		const checklistId = state.select(arrPath).get(["id"]);

		let lambda = new AWS.Lambda({ region: "us-east-1", apiVersion: "2015-03-31" });

		let params = {
			FunctionName: getAiAssistantFunctionName(),
			InvocationType: "RequestResponse",
			LogType: "None",
			Payload: JSON.stringify({
				eventName: "assistantsCreate",
				params: {
					body: {
						instructions:
							"Marv is a factual chatbot that generates a json file that has a four-level structure. Each level has a \"type\" property. At the top level the value of the \"type\" property is \"checklist\". At the second level the value of the \"type\" property is \"list\". At the third level the value of the \"type\" property is \"section\". At the fourth level the value of the type property is \"item\". Each level also has a property named \"children\" which is an array of objects for that next level. A little more context for the objects of the different types. The \"checklist\" level should have a \"name\" property for the name of the activity. It should also have an \"id\" property with a 36 character UUID. As mentioned before it should also have a \"children\" property which is an array of objects of type \"list\". The \"list\" level also should have a \"name\" property for the name of the list. It should also have a \"guid\" property with a 36 character UUID. As mentioned before it should also have a \"children\" property which is an array of objects of type \"section\". The \"section\" level also should have a \"name\" property for the name of the section. It should also have a \"guid\" property with a 36 character UUID. As mentioned before it should also have a \"children\" property which is an array of objects of type \"item\". The \"item\" level should have a property named \"label1\" and also another property named \"label2\". These are typically used in a challenge and response style of checklist where label1 is the challenge and label2 is the response. It should also have a \"guid\" property with a 36 character UUID. This object is the leaf node and should not have any children.",
						name: "Create Assistant - Activity " + checklistId,
						tools: [{ type: "code_interpreter" }, { type: "file_search" }],
						model: "gpt-5.4",
					}
				}
			})
		};

		lambda.invoke(params, function (error, data) {
			if (error) {
				alert(error);
			} else {
				console.log(data.Payload);
				// We will want to set the aiAssistantId in the checklist
				state.select(arrPath).set(["entity", "aiAssistantId"], JSON.parse(data.Payload).id);
				state.commit();
			}
		});
	}).catch((err) => {
		console.error(err);
		showError2("assistantsCreate", err);
	});
}

export function assistantsDelete() {
	assertAiCapabilityEnabled(env.appCapabilities);
	refreshCredentials().then(() => {
		let arrPath = state.select("selectedNodePath").get();
		const asstId = state.select(arrPath).get(["entity", "aiAssistantId"]);

		let lambda = new AWS.Lambda({ region: "us-east-1", apiVersion: "2015-03-31" });

		let params = {
			FunctionName: getAiAssistantFunctionName(),
			InvocationType: "RequestResponse",
			LogType: "None",
			Payload: JSON.stringify({
				eventName: "assistantsDelete",
				params: {
					asst_id: asstId
				}
			})
		};

		lambda.invoke(params, function (error, data) {
			if (error) {
				alert(error);
			} else {
				console.log(data.Payload);

				// We will want to delete the aiAssistantId in the checklist
				state.select(arrPath).set(["entity", "aiAssistantId"], "");
				state.commit();
			}
		});
	}).catch((err) => {
		console.error(err);
		showError2("assistantsDelete", err);
	});
}

export function uploadAiFileToS3(bucket, keyPrefix, file) {
	assertAiCapabilityEnabled(env.appCapabilities);
	return refreshCredentials().then(() => {
		return new Promise((resolve, reject) => {
			// Instantiate aws sdk service objects now that the credentials have been updated.
			let myBucket = new AWS.S3({ useDualStack: true, params: { Bucket: bucket } });

			let params = { Key: keyPrefix + "/" + file.name, ContentType: file.type, Body: file };
			myBucket.upload(params, function (err) {
				let result = err ? "ERROR!" : "SAVED.";
				console.log(result);

				if (err) {
					reject(err);
					return;
				}

				let lambda = new AWS.Lambda({ region: "us-east-1", apiVersion: "2015-03-31" });

				let lambdaParams = {
					FunctionName: getAiAssistantFunctionName(),
					InvocationType: "RequestResponse",
					LogType: "None",
					Payload: JSON.stringify({
						eventName: "filesCreate",
						params: {
							bucket: bucket,
							key: keyPrefix + "/" + file.name
						}
					})
				};

				lambda.invoke(lambdaParams, function (error, data) {
					if (error) {
						alert(error);
						reject(error);
						return;
					}

					try {
						console.log(data.Payload);
						const parsedPayload = JSON.parse(data.Payload);
						const filesPath = ["tree", "root", "entity", "aiAssistantFiles"];
						const currentFiles = Array.isArray(state.get(filesPath)) ? state.get(filesPath) : [];
						const nextFiles = currentFiles
							.filter((item) => item && item.id !== parsedPayload.id)
							.concat(parsedPayload)
							.sort((a, b) => ((a && a.filename) || "").localeCompare((b && b.filename) || ""));

						state.set(filesPath, nextFiles);
						state.commit();
						resolve(parsedPayload);
					} catch (parseErr) {
						reject(parseErr);
					}
				});
			});
		});
	}).catch((err) => {
		console.error(err);
		showError2("uploadAiFileToS3", err);
		throw err;
	});
}

export function filesDelete(fileId) {
	assertAiCapabilityEnabled(env.appCapabilities);
	refreshCredentials().then(() => {
		let lambda = new AWS.Lambda({ region: "us-east-1", apiVersion: "2015-03-31" });

		let params = {
			FunctionName: getAiAssistantFunctionName(),
			InvocationType: "RequestResponse",
			LogType: "None",
			Payload: JSON.stringify({
				eventName: "filesDelete",
				params: {
					file_id: fileId
				}
			})
		};

		lambda.invoke(params, function (error, data) {
			if (error) {
				alert(error);
			} else {
				console.log(data.Payload);

				// Let's find this file_id in array and splice it out
				let arrPath = state.select("selectedNodePath").get();

				const files = state.select(arrPath).get(["entity", "aiAssistantFiles"]);
				const newFiles = [...files];
				const newFilesFiltered = newFiles.filter((file) => {
					return file.id !== fileId;
				});

				// Sort new files by filename
				newFilesFiltered.sort((a, b) => (a.filename > b.filename) ? 1 : -1);

				state.select(arrPath).set(["entity", "aiAssistantFiles"], newFilesFiltered);
				state.commit();
			}
		});
	}).catch((err) => {
		console.error(err);
		showError2("filesDelete", err);
	});
}

export function assistantsFilesCreate(asstId, fileId) {
	assertAiCapabilityEnabled(env.appCapabilities);
	refreshCredentials().then(() => {
		let lambda = new AWS.Lambda({ region: "us-east-1", apiVersion: "2015-03-31" });

		let params = {
			FunctionName: getAiAssistantFunctionName(),
			InvocationType: "RequestResponse",
			LogType: "None",
			Payload: JSON.stringify({
				eventName: "assistantsFilesCreate",
				params: {
					asst_id: asstId,
					body: {
						tool_resources: {
							code_interpreter: {
								file_ids: [fileId]  // Attach file to Code Interpreter
							}
						}
					}
				}
			})
		};

		lambda.invoke(params, function (error, data) {
			if (error) {
				alert(error);
			} else {
				console.log(data.Payload);

				let arrPath = state.select("selectedNodePath").get();
				const newFiles = [...state.select(arrPath).get(["entity", "aiAssistantChecklistFiles"])];

				const resultPayload = JSON.parse(data.Payload);
				const fileIds = resultPayload.tool_resources.code_interpreter.file_ids;

				if (!newFiles.includes(fileIds[0])) {
					newFiles.push(fileIds[0]);
				}

				// Still need to add to assistants files as well
				state.select(arrPath).set(["entity", "aiAssistantChecklistFiles"], newFiles);
				state.commit();
			}
		});
	}).catch((err) => {
		console.error(err);
		showError2("assistantsFilesCreate", err);
	});
}

export function assistantsFilesDelete(asstId, fileId) {
	assertAiCapabilityEnabled(env.appCapabilities);
	refreshCredentials().then(() => {
		let lambda = new AWS.Lambda({ region: "us-east-1", apiVersion: "2015-03-31" });

		let params = {
			FunctionName: getAiAssistantFunctionName(),
			InvocationType: "RequestResponse",
			LogType: "None",
			Payload: JSON.stringify({
				eventName: "assistantsFilesDelete",
				params: {
					asst_id: asstId,
					file_id: fileId
				}
			})
		};

		lambda.invoke(params, function (error, data) {
			if (error) {
				alert(error);
			} else {
				console.log(data.Payload);

				// Let's find this file_id in array and splice it out
				let arrPath = state.select("selectedNodePath").get();

				const files = state.select(arrPath).get(["entity", "aiAssistantChecklistFiles"]);
				const newFiles = [...files];
				const newFilesFiltered = newFiles.filter((file) => {
					return file !== fileId;
				});

				state.select(arrPath).set(["entity", "aiAssistantChecklistFiles"], newFilesFiltered);
				state.commit();
			}
		});
	}).catch((err) => {
		console.error(err);
		showError2("assistantsFilesDelete", err);
	});
}

export async function getChatCompletions(content) {
	assertAiCapabilityEnabled(env.appCapabilities);
	try {
		const chatCompletions = await fetch(`${env.apiGateway.baseUrl}${env.apiGateway.endpoints.openAi.chatCompletions}`, {
			method: "POST",
			body: content,
			headers: {
				Authorization: getJwtToken(),
				"Content-Type": "application/json"
			}
		});
		let jsonResponse = await chatCompletions.json();

		// Helpers to pull a useful content string from a variety of shapes
		const extractContent = (payload) => {
			if (!payload) { return ""; }
			if (typeof payload === "string") { return payload; }
			return (
				payload?.content ||
				payload?.message?.content ||
				payload?.choices?.[0]?.message?.content ||
				payload?.output?.content ||
				""
			);
		};

		// Some gateways might already return a JSON string; try to parse once more if needed
		if (typeof jsonResponse === "string") {
			try {
				jsonResponse = JSON.parse(jsonResponse);
			} catch (_) {
				// keep original string
			}
		}

		const contentStr = extractContent(jsonResponse);

		// If we extracted a real string, return it; otherwise fall back to a pretty JSON dump
		return contentStr || JSON.stringify(jsonResponse, null, 2);

	} catch (err) {
		console.error(err);
		showError2("getChatCompletions", err);
	}
}

export async function getImagesGenerate(content) {
	assertAiCapabilityEnabled(env.appCapabilities);
	const imagesGenerate = await fetch(`${env.apiGateway.baseUrl}${env.apiGateway.endpoints.openAi.imagesGenerate}`, {
		method: "POST",
		body: JSON.stringify({ prompt: content, model: DEFAULT_IMAGE_EDIT_MODEL }),
		headers: {
			Authorization: getJwtToken(),
			"Content-Type": "application/json"
		}
	});
	const jsonResponse = await imagesGenerate.json();

	// dall-e models answer with a URL; gpt-image models answer with base64 PNG data.
	if (Array.isArray(jsonResponse) && jsonResponse[0]) {
		if (jsonResponse[0].url) {
			return jsonResponse[0].url;
		}
		if (jsonResponse[0].b64_json) {
			return `data:image/png;base64,${jsonResponse[0].b64_json}`;
		}
	}
	return "";
}

function fileReaderToDataUrl(blob) {
	return new Promise((resolve, reject) => {
		if (!blob) {
			reject(new Error("No image blob was provided."));
			return;
		}

		const reader = new FileReader();
		reader.onload = () => resolve(typeof reader.result === "string" ? reader.result : "");
		reader.onerror = () => reject(new Error("Unable to read edited image data."));
		reader.readAsDataURL(blob);
	});
}

function getImageMimeTypeForOutputFormat(outputFormat) {
	switch (`${outputFormat || ""}`.toLowerCase()) {
		case "jpeg":
		case "jpg":
			return "image/jpeg";
		case "webp":
			return "image/webp";
		case "png":
		default:
			return "image/png";
	}
}

function getImageMimeTypeForBase64(base64 = "", fallbackOutputFormat = "png") {
	const prefix = `${base64 || ""}`.slice(0, 16);
	if (prefix.indexOf("/9j/") === 0) {
		return "image/jpeg";
	}
	if (prefix.indexOf("iVBOR") === 0) {
		return "image/png";
	}
	if (prefix.indexOf("UklGR") === 0) {
		return "image/webp";
	}
	return getImageMimeTypeForOutputFormat(fallbackOutputFormat);
}

function normalizeImageStorage(storage = {}) {
	if (!storage || typeof storage !== "object") {
		return null;
	}

	const key = storage.key || storage.Key || storage.path || storage.Path || "";
	if (!key) {
		return null;
	}

	return {
		bucket: storage.bucket || storage.Bucket || "",
		key,
		path: key,
		contentType: storage.contentType || storage.content_type || storage.ContentType || "",
		fileName: storage.fileName || storage.file_name || storage.name || ""
	};
}

function extractImageEditResult(payload, outputFormat = "png") {
	let normalizedPayload = payload;
	if (typeof normalizedPayload === "string") {
		try {
			normalizedPayload = JSON.parse(normalizedPayload);
		} catch (err) {
			if (/^data:image\//.test(normalizedPayload)) {
				return { dataUrl: normalizedPayload, raw: payload };
			}
			if (/^https?:\/\//.test(normalizedPayload)) {
				return { url: normalizedPayload, raw: payload };
			}
			return null;
		}
	}

	const candidates = [];
	if (Array.isArray(normalizedPayload)) {
		candidates.push(...normalizedPayload);
	}
	if (Array.isArray(normalizedPayload?.data)) {
		candidates.push(...normalizedPayload.data);
	}
	if (normalizedPayload?.image) {
		candidates.push(normalizedPayload.image);
	}
	if (normalizedPayload) {
		candidates.push(normalizedPayload);
	}

	for (const candidate of candidates) {
		if (!candidate) {
			continue;
		}

		if (typeof candidate === "string") {
			if (/^data:image\//.test(candidate)) {
				return { dataUrl: candidate, raw: normalizedPayload };
			}
			if (/^https?:\/\//.test(candidate)) {
				return { url: candidate, raw: normalizedPayload };
			}
			continue;
		}

		const storage = normalizeImageStorage(
			candidate.storage ||
			candidate.s3 ||
			candidate.output_s3 ||
			candidate.outputS3 ||
			candidate.image_s3 ||
			candidate.imageS3
		);
		if (storage) {
			return {
				storage,
				url: candidate.signedUrl || candidate.signed_url || candidate.url || candidate.image_url || "",
				raw: normalizedPayload
			};
		}
		if (typeof candidate.data_url === "string" && candidate.data_url) {
			return { dataUrl: candidate.data_url, raw: normalizedPayload };
		}
		if (typeof candidate.image_url === "string" && candidate.image_url) {
			return { url: candidate.image_url, raw: normalizedPayload };
		}
		if (typeof candidate.url === "string" && candidate.url) {
			return { url: candidate.url, raw: normalizedPayload };
		}
		if (typeof candidate.b64_json === "string" && candidate.b64_json) {
			return {
				dataUrl: `data:${getImageMimeTypeForBase64(candidate.b64_json, outputFormat)};base64,${candidate.b64_json}`,
				raw: normalizedPayload
			};
		}
	}

	return null;
}

async function urlToImageDataUrl(url) {
	const response = await fetch(url);
	if (!response.ok) {
		throw new Error(`Unable to download edited image (${response.status}).`);
	}

	const blob = await response.blob();
	return fileReaderToDataUrl(blob);
}

export async function getImagesEdit({
	prompt = "",
	imageBlob,
	imageS3,
	maskBlob,
	imageName = "image-edited.png",
	model = DEFAULT_IMAGE_EDIT_MODEL,
	size,
	background,
	quality,
	outputFormat,
	outputCompression,
	outputS3,
	referenceImages = [],
	responseFormat,
	showAlert = true
} = {}) {
	assertAiCapabilityEnabled(env.appCapabilities);
	const trimmedPrompt = `${prompt || ""}`.trim();
	if (!trimmedPrompt) {
		throw new Error("An AI image edit prompt is required.");
	}
	if (!imageBlob && !imageS3) {
		throw new Error("An image is required for AI editing.");
	}

	const requestBody = {
		model,
		prompt: trimmedPrompt,
		image: imageS3 ? {
			s3: imageS3,
			file_name: imageS3.fileName || imageS3.file_name || imageName,
			content_type: imageS3.contentType || imageS3.content_type || "image/jpeg"
		} : {
			data_url: await fileReaderToDataUrl(imageBlob),
			file_name: imageName,
			content_type: imageBlob.type || "image/png"
		}
	};

	if (responseFormat) {
		requestBody.response_format = responseFormat;
	}

	if (maskBlob) {
		requestBody.mask = {
			data_url: await fileReaderToDataUrl(maskBlob),
			file_name: imageName.replace(/\.png$/i, "-mask.png"),
			content_type: maskBlob.type || "image/png"
		};
	}

	if (size) {
		requestBody.size = size;
	}
	if (quality) {
		requestBody.quality = quality;
	}
	if (outputFormat) {
		requestBody.output_format = outputFormat;
	}
	if (outputCompression !== undefined && outputCompression !== null) {
		requestBody.output_compression = outputCompression;
	}
	if (outputS3) {
		requestBody.output_s3 = outputS3;
	}
	if (Array.isArray(referenceImages) && referenceImages.length) {
		requestBody.reference_images = referenceImages.map((image, index) => ({
			s3: image.s3,
			file_name: image.file_name || image.fileName || image.s3?.file_name || image.s3?.fileName || `reference-${index + 1}.png`,
			content_type: image.content_type || image.contentType || image.s3?.content_type || image.s3?.contentType || "image/png",
			label: image.label || "",
			folder: image.folder || ""
		}));
	}
	if (background) {
		requestBody.background = background;
	}

	await refreshCredentials();

	const lambda = new AWS.Lambda({ region: env.aws?.defaultRegion || "us-east-1", apiVersion: "2015-03-31" });
	const params = {
		FunctionName: getAiAssistantFunctionName(),
		InvocationType: "RequestResponse",
		LogType: "None",
		Payload: JSON.stringify({
			eventName: "imagesEditCreate",
			params: {
				body: requestBody
			}
		})
	};

	const parsedResponse = await new Promise((resolve, reject) => {
		lambda.invoke(params, (error, data) => {
			if (error) {
				console.error(error);
				if (showAlert) {
					showError2("getImagesEdit", error);
				}
				return reject(error);
			}

			if (data && data.FunctionError) {
				try {
					const errObj = JSON.parse(data.Payload || "{}");
					const errMsg = errObj.errorMessage || errObj.errorType || "Lambda function error";
					return reject(new Error(errMsg));
				} catch (_) {
					return reject(new Error("Lambda function error"));
				}
			}

			let payload = data?.Payload;
			try {
				if (typeof payload === "string") {
					payload = JSON.parse(payload);
				}
			} catch (_) {
				// keep raw payload if JSON parse fails
			}

			if (payload && typeof payload === "object" && (payload.errorMessage || payload.errorType)) {
				const errMsg = payload.errorMessage || payload.errorType || "Lambda function error payload";
				return reject(new Error(errMsg));
			}

			return resolve(payload);
		});
	});

	const imageResult = extractImageEditResult(parsedResponse, outputFormat);
	if (!imageResult) {
		throw new Error("AI image edit did not return an edited image.");
	}

	if (imageResult.dataUrl || imageResult.storage) {
		return imageResult;
	}

	return {
		...imageResult,
		dataUrl: await urlToImageDataUrl(imageResult.url)
	};
}

// Direct Lambda-based chat completions (bypasses API Gateway timeout limits)
export async function getChatCompletionsLambda(content) {
	assertAiCapabilityEnabled(env.appCapabilities);
	const bodyObj = typeof content === "string" ? JSON.parse(content) : content;

	const extractContent = (payload) => {
		if (!payload) { return ""; }
		if (typeof payload === "string") { return payload; }
		return (
			payload?.content ||
			payload?.message?.content ||
			payload?.choices?.[0]?.message?.content ||
			payload?.output?.content ||
			""
		);
	};

	try {
		await refreshCredentials();

		const lambda = new AWS.Lambda({ region: env.aws?.defaultRegion || "us-east-1", apiVersion: "2015-03-31" });
		const params = {
			FunctionName: getAiAssistantFunctionName(),
			InvocationType: "RequestResponse",
			LogType: "None",
			Payload: JSON.stringify({
				eventName: "chatCompletionsCreate",
				params: { body: bodyObj }
			})
		};

		return await new Promise((resolve, reject) => {
			lambda.invoke(params, (error, data) => {
				if (error) {
					console.error(error);
					showError2("getChatCompletionsLambda", error);
					return reject(error);
				}

				if (data && data.FunctionError === "Handled") {
					try {
						const errObj = JSON.parse(data.Payload || "{}");
						const errMsg = errObj.errorMessage || "Lambda handled error";
						return reject(new Error(errMsg));
					} catch (_) {
						return reject(new Error("Lambda handled error"));
					}
				}

				let payload = data?.Payload;
				try {
					if (typeof payload === "string") {
						payload = JSON.parse(payload);
					}
				} catch (_) {
					// keep raw payload if JSON parse fails
				}

				const contentStr = extractContent(payload);
				if (contentStr && typeof contentStr === "string") {
					return resolve(contentStr);
				}
				try {
					return resolve(JSON.stringify(payload, null, 2));
				} catch (_) {
					return resolve(String(payload));
				}
			});
		});
	} catch (err) {
		console.error(err);
		showError2("getChatCompletionsLambda", err);
		throw err;
	}
}

export async function getAudioTranscriptionsLambda(content) {
	assertAiCapabilityEnabled(env.appCapabilities);
	const bodyObj = typeof content === "string" ? JSON.parse(content) : content;

	const extractContent = (payload) => {
		if (!payload) { return ""; }
		if (typeof payload === "string") { return payload; }
		return (
			payload?.text ||
			payload?.transcript ||
			payload?.content ||
			payload?.message?.content ||
			payload?.choices?.[0]?.message?.content ||
			payload?.output?.content ||
			""
		);
	};

	try {
		await refreshCredentials();

		const lambda = new AWS.Lambda({ region: env.aws?.defaultRegion || "us-east-1", apiVersion: "2015-03-31" });
		const params = {
			FunctionName: getAiAssistantFunctionName(),
			InvocationType: "RequestResponse",
			LogType: "None",
			Payload: JSON.stringify({
				eventName: "audioTranscriptionsCreate",
				params: { body: bodyObj }
			})
		};

		return await new Promise((resolve, reject) => {
			lambda.invoke(params, (error, data) => {
				if (error) {
					console.error(error);
					showError2("getAudioTranscriptionsLambda", error);
					return reject(error);
				}

				if (data && data.FunctionError === "Handled") {
					try {
						const errObj = JSON.parse(data.Payload || "{}");
						const errMsg = errObj.errorMessage || "Lambda handled error";
						return reject(new Error(errMsg));
					} catch (_) {
						return reject(new Error("Lambda handled error"));
					}
				}

				let payload = data?.Payload;
				try {
					if (typeof payload === "string") {
						payload = JSON.parse(payload);
					}
				} catch (_) {
					// keep raw payload if JSON parse fails
				}

				if (payload === true) {
					return reject(new Error("Audio transcription is not available in aiAssistant lambda yet."));
				}

				const contentStr = extractContent(payload);
				if (contentStr && typeof contentStr === "string") {
					return resolve(contentStr);
				}
				try {
					return resolve(JSON.stringify(payload, null, 2));
				} catch (_) {
					return resolve(String(payload));
				}
			});
		});
	} catch (err) {
		console.error(err);
		showError2("getAudioTranscriptionsLambda", err);
		throw err;
	}
}

export async function getImageDescriptionsLambda(content) {
	assertAiCapabilityEnabled(env.appCapabilities);
	const bodyObj = typeof content === "string" ? JSON.parse(content) : content;

	const extractContent = (payload) => {
		if (!payload) { return ""; }
		if (typeof payload === "string") { return payload; }
		return (
			payload?.text ||
			payload?.description ||
			payload?.content ||
			payload?.message?.content ||
			payload?.choices?.[0]?.message?.content ||
			payload?.output?.content ||
			""
		);
	};

	try {
		await refreshCredentials();

		const lambda = new AWS.Lambda({ region: env.aws?.defaultRegion || "us-east-1", apiVersion: "2015-03-31" });
		const params = {
			FunctionName: getAiAssistantFunctionName(),
			InvocationType: "RequestResponse",
			LogType: "None",
			Payload: JSON.stringify({
				eventName: "imageDescriptionsCreate",
				params: { body: bodyObj }
			})
		};

		return await new Promise((resolve, reject) => {
			lambda.invoke(params, (error, data) => {
				if (error) {
					console.error(error);
					showError2("getImageDescriptionsLambda", error);
					return reject(error);
				}

				if (data && data.FunctionError) {
					try {
						const errObj = JSON.parse(data.Payload || "{}");
						const errMsg = errObj.errorMessage || errObj.errorType || "Lambda function error";
						return reject(new Error(errMsg));
					} catch (_) {
						return reject(new Error("Lambda function error"));
					}
				}

				let payload = data?.Payload;
				try {
					if (typeof payload === "string") {
						payload = JSON.parse(payload);
					}
				} catch (_) {
					// keep raw payload if JSON parse fails
				}

				if (payload === true) {
					return reject(new Error("Image description is not available in aiAssistant lambda yet."));
				}

				if (payload && typeof payload === "object" && (payload.errorMessage || payload.errorType)) {
					const errMsg = payload.errorMessage || payload.errorType || "Lambda function error payload";
					return reject(new Error(errMsg));
				}

				const contentStr = extractContent(payload);
				if (contentStr && typeof contentStr === "string") {
					return resolve(contentStr);
				}
				try {
					return resolve(JSON.stringify(payload, null, 2));
				} catch (_) {
					return resolve(String(payload));
				}
			});
		});
	} catch (err) {
		console.error(err);
		showError2("getImageDescriptionsLambda", err);
		throw err;
	}
}

export async function getResponsesCreateLambda(content, options = {}) {
	assertAiCapabilityEnabled(env.appCapabilities);
	const bodyObj = typeof content === "string" ? JSON.parse(content) : content;
	const showAlert = options.showAlert !== false;
	const eventName = options.eventName || "responsesCreate";

	try {
		await refreshCredentials();

		const lambda = new AWS.Lambda({ region: env.aws?.defaultRegion || "us-east-1", apiVersion: "2015-03-31" });
		const params = {
			FunctionName: getAiAssistantFunctionName(),
			InvocationType: "RequestResponse",
			LogType: "None",
			Payload: JSON.stringify({
				eventName,
				params: { body: bodyObj }
			})
		};

		return await new Promise((resolve, reject) => {
			lambda.invoke(params, (error, data) => {
				if (error) {
					console.error(error);
					if (showAlert) {
						showError2("getResponsesCreateLambda", error);
					}
					return reject(error);
				}

				if (data && data.FunctionError) {
					try {
						const errObj = JSON.parse(data.Payload || "{}");
						const errMsg = errObj.errorMessage || errObj.errorType || "Lambda function error";
						return reject(new Error(errMsg));
					} catch (_) {
						return reject(new Error("Lambda function error"));
					}
				}

				let payload = data?.Payload;
				try {
					if (typeof payload === "string") {
						payload = JSON.parse(payload);
					}
				} catch (_) {
					// keep raw payload if JSON parse fails
				}

				// Some lambda runtimes/layers can return an error-like payload
				// without setting FunctionError in the invoke envelope.
				if (payload && typeof payload === "object" && (payload.errorMessage || payload.errorType)) {
					const errMsg = payload.errorMessage || payload.errorType || "Lambda function error payload";
					return reject(new Error(errMsg));
				}

				return resolve(payload);
			});
		});
	} catch (err) {
		console.error(err);
		if (showAlert) {
			showError2("getResponsesCreateLambda", err);
		}
		throw err;
	}
}

export async function getPdfResponsesLambda(content, options = {}) {
	const payload = await getResponsesCreateLambda(content, {
		...options,
		eventName: "pdfResponsesCreate"
	});
	if (payload === true) {
		throw new Error("PDF responses are not available in aiAssistant lambda yet.");
	}
	return payload;
}

export async function getAwsTranscribeLambda(content, options = {}) {
	assertAiCapabilityEnabled(env.appCapabilities);
	const bodyObj = typeof content === "string" ? JSON.parse(content) : content;
	const showAlert = options.showAlert !== false;

	try {
		await refreshCredentials();

		const lambda = new AWS.Lambda({ region: env.aws?.defaultRegion || "us-east-1", apiVersion: "2015-03-31" });
		const params = {
			FunctionName: getAiAssistantFunctionName(),
			InvocationType: "RequestResponse",
			LogType: "None",
			Payload: JSON.stringify({
				eventName: "awsTranscribeCreate",
				params: { body: bodyObj }
			})
		};

		return await new Promise((resolve, reject) => {
			lambda.invoke(params, (error, data) => {
				if (error) {
					console.error(error);
					if (showAlert) {
						showError2("getAwsTranscribeLambda", error);
					}
					return reject(error);
				}

				if (data && data.FunctionError) {
					try {
						const errObj = JSON.parse(data.Payload || "{}");
						const errMsg = errObj.errorMessage || errObj.errorType || "Lambda function error";
						return reject(new Error(errMsg));
					} catch (_) {
						return reject(new Error("Lambda function error"));
					}
				}

				let payload = data?.Payload;
				try {
					if (typeof payload === "string") {
						payload = JSON.parse(payload);
					}
				} catch (_) {
					// keep raw payload if JSON parse fails
				}

				if (payload === true) {
					return reject(new Error("AWS Transcribe is not available in aiAssistant lambda yet."));
				}

				if (payload && typeof payload === "object" && (payload.errorMessage || payload.errorType)) {
					const errMsg = payload.errorMessage || payload.errorType || "Lambda function error payload";
					return reject(new Error(errMsg));
				}

				return resolve(payload);
			});
		});
	} catch (err) {
		console.error(err);
		if (showAlert) {
			showError2("getAwsTranscribeLambda", err);
		}
		throw err;
	}
}

export async function getRekognitionTagsLambda(content, options = {}) {
	assertAiCapabilityEnabled(env.appCapabilities);
	const bodyObj = typeof content === "string" ? JSON.parse(content) : content;
	const showAlert = options.showAlert !== false;

	try {
		await refreshCredentials();

		const lambda = new AWS.Lambda({ region: env.aws?.defaultRegion || "us-east-1", apiVersion: "2015-03-31" });
		const params = {
			FunctionName: getAiAssistantFunctionName(),
			InvocationType: "RequestResponse",
			LogType: "None",
			Payload: JSON.stringify({
				eventName: "rekognitionTagsCreate",
				params: { body: bodyObj }
			})
		};

		return await new Promise((resolve, reject) => {
			lambda.invoke(params, (error, data) => {
				if (error) {
					console.error(error);
					if (showAlert) {
						showError2("getRekognitionTagsLambda", error);
					}
					return reject(error);
				}

				if (data && data.FunctionError) {
					try {
						const errObj = JSON.parse(data.Payload || "{}");
						const errMsg = errObj.errorMessage || errObj.errorType || "Lambda function error";
						return reject(new Error(errMsg));
					} catch (_) {
						return reject(new Error("Lambda function error"));
					}
				}

				let payload = data?.Payload;
				try {
					if (typeof payload === "string") {
						payload = JSON.parse(payload);
					}
				} catch (_) {
					// keep raw payload if JSON parse fails
				}

				if (payload === true) {
					return reject(new Error("AWS Rekognition tagging is not available in aiAssistant lambda yet."));
				}

				if (payload && typeof payload === "object" && (payload.errorMessage || payload.errorType)) {
					const errMsg = payload.errorMessage || payload.errorType || "Lambda function error payload";
					return reject(new Error(errMsg));
				}

				return resolve(payload);
			});
		});
	} catch (err) {
		console.error(err);
		if (showAlert) {
			showError2("getRekognitionTagsLambda", err);
		}
		throw err;
	}
}

export async function getContent(asstId, fileId) {
	assertAiCapabilityEnabled(env.appCapabilities);
	// Create a thread in the assistant
	const createThreadResponse = await fetch(`${env.apiGateway.baseUrl}/services/openai/threads`, {
		method: "POST",
		headers: { Authorization: getJwtToken() }
	});
	const jsonCreateThreadResponse = await createThreadResponse.json();
	const threadId = jsonCreateThreadResponse.id;

	// Add a message to the thread
		const createMessageResponse = await fetch(`${env.apiGateway.baseUrl}/services/openai/threads/${threadId}/messages`, {
			method: "POST",
			headers: {
				Authorization: getJwtToken(),
				"Content-Type": "application/json"
			},
		// body: JSON.stringify({role: "user", content: "Can you return all of the checklist content in a hierarchy of JSON data? I would like all of the content. Not just a summary.", file_ids: [fileId] }) 
			body: JSON.stringify({ role: "user", content: "Can you return all of the checklist content?", file_ids: [fileId] })
			// headers: headers
		});
		await createMessageResponse.json();


	// Add a run
	const createRunResponse = await fetch(`${env.apiGateway.baseUrl}/services/openai/threads/${threadId}/runs`, {
		method: "POST",
		headers: {
			Authorization: getJwtToken(),
			"Content-Type": "application/json"
		},
		body: JSON.stringify({ assistant_id: asstId })
	});
	const jsonCreateRunResponse = await createRunResponse.json();

	// Poll the run until complete-- for now just use timeout

	// This needs fixed before can get good messages back!
	let runInterval;
	let runCount = 0;
	runInterval = setInterval(async () => {
		runCount++;
		const createRunIntervalResponse = await fetch(`${env.apiGateway.baseUrl}/services/openai/threads/${threadId}/runs/${jsonCreateRunResponse.id}`, {
			method: "GET",
			headers: { Authorization: getJwtToken() }
		});
		const jsonCreateRunIntervalResponse = await createRunIntervalResponse.json();

		// console.log("RUN STEP", jsonCreateRunIntervalResponse);

		if (jsonCreateRunIntervalResponse.status === "completed") {
			clearInterval(runInterval);

			// Retrieve messages
			const createRunMessagesResponse = await fetch(`${env.apiGateway.baseUrl}/services/openai/threads/${threadId}/messages`, {
				method: "GET",
				headers: { Authorization: getJwtToken() }
			});
			const jsonCreateRunMessagesResponse = await createRunMessagesResponse.json();

			let robotResponse = "";
			for (let i = jsonCreateRunMessagesResponse.data.length - 1; i >= 0; i--) {
				const item = jsonCreateRunMessagesResponse.data[i];

				robotResponse = robotResponse + item.content[0].text.value + "<br/><br/>";
			}

			robotResponse = robotResponse + "<b style='color: green'>I am done :)</b><br/><br/>";

			state.set(["appState", "aiAssistantResponse"], robotResponse);
		} else {
			// let dot = ".";
			// state.set(["appState", "aiAssistantResponse"], "I am hard at work trying to figure this out" + dot.repeat(runCount));

			try {
				// Retrieve messages
				const createRunMessagesResponse = await fetch(`${env.apiGateway.baseUrl}/services/openai/threads/${threadId}/messages`, {
					method: "GET",
					headers: { Authorization: getJwtToken() }
				});
				const jsonCreateRunMessagesResponse = await createRunMessagesResponse.json();

				let robotResponse = "";
				for (let i = jsonCreateRunMessagesResponse.data.length - 1; i >= 0; i--) {
					const item = jsonCreateRunMessagesResponse.data[i];

					if (item.content.length > 0 && item.content[0].hasOwnProperty("text")) {
						robotResponse = robotResponse + item.content[0].text.value + "<br/><br/>";
					}
				}

				robotResponse = robotResponse + `<b style='color: #0E7AFE'>STILL WORKING${".".repeat(runCount)}</b><br/><br/>`;

				state.set(["appState", "aiAssistantResponse"], robotResponse);

			} catch (err) {
				state.set(["appState", "aiAssistantResponse"], `${err.message}`);
			}
		}
	}, 1000);

	// setTimeout(async () => {
	//     // Retrieve messages
	//     const createRunMessagesResponse = await fetch(`${env.apiGateway.baseUrl}/services/openai/threads/${threadId}/messages`, {
	//         method: 'GET'
	//         // headers: headers
	//     });
	//     const jsonCreateRunMessagesResponse = await createRunMessagesResponse.json();

	//     debugger;
	//     alert(jsonCreateRunMessagesResponse.data[0].content[0].text.value);

	// },10000);
}

export async function getContentStreamed(smd, parser, fileId, assistantId, threadId, message, identityId, activityId, orgId) {
	assertAiCapabilityEnabled(env.appCapabilities);
	const hasOrgRequest = !!(orgId && identityId);
	const hasActivityRequest = !!(identityId && activityId);
	const hasAssistantRequest = !!(assistantId && threadId);

	if (!hasOrgRequest && !hasActivityRequest && !hasAssistantRequest) {
		let newData = state.get(["appState", "aiAssistantResponse"]);
		newData = newData + "\n\n<b style='color: #ff8c8c'>Unable to send assistant message. Missing assistant or thread initialization.</b>\n\n";
		state.set(["appState", "aiAssistantResponse"], newData);
		return;
	}

	if (!aiSocket) {
		// The socket authenticates with the user's ID token: organization chat answers only for organizations
		// the signed-in user is a member of, and an invalid token refuses the connection.
		aiSocket = new WebSocket(
			`wss://kkx5nxjeha.execute-api.us-east-1.amazonaws.com/prod/?identityId=${state.get(["user", "identityId"])}&token=${encodeURIComponent(getJwtToken() || "")}`
		);
	}

	if (!aiSocket.onmessage) {
		aiSocket.onmessage = (event) => {
			const incomingData = typeof event.data === "string" ? event.data : `${event.data || ""}`;
			console.log(incomingData);

			if (incomingData.startsWith("{\"message\": \"Endpoint request timed out\"")) {
				// Timeout messages show up here!
			} else {
				// smd.parser_write(parser, event.data)
				const sanitizedChunk = sanitizeAssistantStreamChunk(incomingData);
				if (sanitizedChunk === "") {
					return;
				}

				let newData = state.get(["appState", "aiAssistantResponse"]);
				newData = newData + sanitizedChunk;

				state.set(["appState", "aiAssistantResponse"], newData);

				let newDataLast = state.get(["appState", "aiAssistantResponseLast"]);
				newDataLast = newDataLast + sanitizedChunk;

				state.set(["appState", "aiAssistantResponseLast"], newDataLast);
			}
		};
	}

	if (!aiSocket.onopen) {
		aiSocket.onopen = () => {
			resetAiStreamSanitizer();
			if (orgId && identityId) {
				aiSocket.send(`{ "action": "sendOrgAssistantMessage", "message": "${escapeForJSON(message)}", "userId": "${identityId}", "orgId": "${orgId}" }`);
			} else if (identityId && activityId) {
				aiSocket.send(`{ "action": "sendAssistantMessage", "message": "${escapeForJSON(message)}", "userId": "${identityId}", "activityId": "${activityId}" }`);
			} else {
				aiSocket.send(`{ "action": "sendAssistantMessage", "assistantId": "${assistantId}", "threadId": "${threadId}", "message": "${escapeForJSON(message)}" }`);
			}
		};
	} else {
		resetAiStreamSanitizer();
		if (orgId && identityId) {
			aiSocket.send(`{ "action": "sendOrgAssistantMessage", "message": "${escapeForJSON(message)}", "userId": "${identityId}", "orgId": "${orgId}" }`);
		} else if (identityId && activityId) {
			aiSocket.send(`{ "action": "sendAssistantMessage", "message": "${escapeForJSON(message)}", "userId": "${identityId}", "activityId": "${activityId}" }`);
		} else {
			aiSocket.send(`{ "action": "sendAssistantMessage", "assistantId": "${assistantId}", "threadId": "${threadId}", "message": "${escapeForJSON(message)}" }`);
		}
	}

	if (!aiSocket.onclose) {
		aiSocket.onclose = () => {
			aiSocket = null;
		};
	}

	if (!aiSocket.onerror) {
		aiSocket.onerror = () => {
			let newData = state.get(["appState", "aiAssistantResponse"]);
			newData = newData + "\n\nUnable to reach the AI assistant. Reload the page and try again.\n\n";

			state.set(["appState", "aiAssistantResponse"], newData);
		};
	}

}

function escapeForJSON(str) {
	return str
		.replace(/\\/g, "\\\\")  // Escape backslashes first!
		.replace(/"/g, "\\\"")     // Escape double quotes
		.replace(/\n/g, "\\n")    // Escape newlines
		.replace(/\r/g, "\\r")    // Escape carriage returns
		.replace(/\t/g, "\\t")    // Escape tabs
		// .replace(/\b/g, "\\b")    // Escape backspaces
		.replace(/\f/g, "\\f");   // Escape form feeds
}
